import { useCallback } from 'react';
import api from '../../services/api';
import { useSnackbar } from '../../components/common/Snackbar';
import { normalizeLifecycleValue, prepareStatusLabelForStorage } from '../../utils/editQueryModalUtils';
import { queryService } from '../../services/queryService';

/**
 * Hook to manage form validation and submission for EditQueryModal
 * @param {Object} params - Hook parameters
 * @param {Object} params.formData - Form data object
 * @param {Object} params.query - Query object being edited
 * @param {Function} params.setErrors - Setter for errors
 * @param {Function} params.setLoading - Setter for loading state
 * @param {Function} params.onSuccess - Success callback
 * @param {Function} params.onClose - Close callback
 * @returns {Function} Submit handler function
 */
export const useEditQueryModalForm = ({
  formData,
  query,
  newFiles,
  removedAttachments,
  setErrors,
  setLoading,
  onSuccess,
  onClose,
}) => {
  const { showSnackbar } = useSnackbar();

  const validate = useCallback(() => {
    const newErrors = {};
    if (!formData.queryDescription) newErrors.queryDescription = 'Query description is required';
    if (!formData.assigneeId) newErrors.assigneeId = 'Assignee is required';
    return newErrors;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      header: '', // Title field removed from UI, sending empty string to satisfy backend validation
      queryDescription: formData.queryDescription,
      visualDescription: formData.visualDescription,
      priority: formData.priority,
      category: formData.category,
      statusResponse: formData.statusResponse,
      assignee: formData.assigneeId || null,
    };

    setLoading(true);
    try {
      // Get existing status to preserve it
      const existingStatus = normalizeLifecycleValue(query.statusLabel || query.status || 'raised');
      const statusLabelForStorage = prepareStatusLabelForStorage(existingStatus);

      const formPayload = new FormData();
      formPayload.append('header', payload.header);
      formPayload.append('queryDescription', payload.queryDescription || '');
      formPayload.append('visualDescription', payload.visualDescription || '');
      formPayload.append('priority', payload.priority || '');
      formPayload.append('category', payload.category || '');
      formPayload.append('statusResponse', payload.statusResponse || '');
      formPayload.append('assignee', payload.assignee || '');
      formPayload.append('statusLabel', statusLabelForStorage);

      newFiles.forEach((file) => {
        formPayload.append('attachments', file);
      });

      formPayload.append('removedAttachments', JSON.stringify(removedAttachments || []));

      await api.put(`/queries/${query._id}`, formPayload, {
        transformRequest: [(data, headers) => {
          if (data instanceof FormData) {
            delete headers['Content-Type'];
          }
          return data;
        }],
      });

      // Refetch latest query so the UI reflects changes immediately (no page reload needed)
      let updatedQuery = null;
      try {
        updatedQuery = await queryService.getQueryById(query._id);
      } catch (e) {
        // If refetch fails, fall back to the submitted payload so the UI still updates instantly
        updatedQuery = null;
      }
      showSnackbar('Query updated successfully', 'success');
      onSuccess?.(updatedQuery || { ...(query || {}), ...payload });
      onClose();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update query';
      showSnackbar(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [
    formData,
    query,
    validate,
    newFiles,
    removedAttachments,
    setErrors,
    setLoading,
    onSuccess,
    onClose,
    showSnackbar,
  ]);

  return {
    handleSubmit,
    validate,
  };
};
